/**
 * 配信一覧の「直近のやりとり」カード（設計書 9.3 / html 図6）。
 *
 * 設計書: `docs/design-docs/deliveries/open-click-analytics.md`。
 * 本文中の `設計書 N.M` はこの md、`html N.M` / `html 図N` は同ディレクトリの
 * `open-click-analytics.html` を指す。
 */
import { LegendItem } from "components/LegendItem";
import { TabButton } from "components/TabButton";
import { Loading } from "components/ui/Loading/Loading";
import {
  ACTIVITY_DOT,
  ATTENTION_COLOR,
  BREAKDOWN_BAND_COLORS,
  type CompanyState,
  companyStateLabel,
  INTEREST_CHIP,
  NO_DATA_COLOR,
  nextActionFor,
  REACTION_SCALE,
} from "constants/delivery-analytics";
import {
  type CompanyActivityItem,
  type CompanyActivitySort,
  useCompanyActivityApi,
} from "hooks/use-delivery-api";
import { cn } from "lib/utils";
import { Flag } from "lucide-react";
import { type FC, useState } from "react";
import { Link } from "react-router-dom";
import {
  companyReactionStep,
  formatCount,
  formatRate,
} from "utils/delivery-analytics";
import { formatRelativeDay } from "utils/relative-time";

const PERIOD_OPTIONS: ReadonlyArray<number> = [30, 90, 180];

const SORT_OPTIONS: ReadonlyArray<{
  value: CompanyActivitySort;
  label: string;
}> = [
  { value: "interest", label: "関心度順" },
  { value: "lapsed", label: "途絶えた順" },
  { value: "not_delivered", label: "まだ送っていない" },
];

const REACTION_TYPE_LABEL: Record<string, string> = {
  open: "開封",
  click: "クリック",
  reply: "返信",
};

/**
 * 配信一覧の「直近のやりとり」カード（設計書 9.3 / html 図6）。
 * 会社を 4 状態に分類し、会社別の関心度・推移・次の一手を出す。
 * @return {JSX.Element} カード
 */
export const RecentEngagementCard: FC = () => {
  const [days, setDays] = useState(90);
  const [sort, setSort] = useState<CompanyActivitySort>("interest");
  const { data, loading } = useCompanyActivityApi({ days, sort, limit: 20 });

  const counts = data?.counts;
  const denominator = counts?.denominator ?? 0;
  // 帯の幅は分母 0 でも 0% を入れるしかないが、凡例の表示は `formatRate` に通して
  // 分母 0 を `—` にする（設計書 3.1: 0% と「計測できていない」は区別する）。
  const shareWidth = (value: number): number =>
    denominator > 0 ? (value * 100) / denominator : 0;
  const shareRate = (value: number): number | null =>
    denominator > 0 ? value / denominator : null;

  // 帯と凡例は同じ 1 本から出す（色は `BREAKDOWN_BAND_COLORS` の同じ添字）
  const bands: ReadonlyArray<{
    state: CompanyState;
    value: number;
    flag?: boolean;
  }> = [
    { state: "engaged", value: counts?.engaged ?? 0 },
    { state: "lapsed", value: counts?.lapsed ?? 0, flag: true },
    { state: "never_responded", value: counts?.neverResponded ?? 0 },
  ];

  return (
    <div className="mb-6 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-semibold">直近のやりとり</h2>
        <div className="flex gap-4 border-b border-slate-200">
          {PERIOD_OPTIONS.map(option => (
            <TabButton
              key={option}
              active={option === days}
              onClick={() => setDays(option)}
            >
              {option}日
            </TabButton>
          ))}
        </div>
      </div>

      {loading || !counts ? (
        <div className="flex justify-center py-6">
          <Loading size="sm" />
        </div>
      ) : (
        <>
          <div className="mt-3 flex flex-wrap gap-x-8 gap-y-3">
            <Metric
              label="やりとりのある会社"
              value={
                <>
                  {formatCount(counts.engaged)}{" "}
                  <span className="text-xs font-medium text-base-content/50">
                    / {formatCount(denominator)} 社
                  </span>
                </>
              }
            />
            <Metric
              label={
                <span className="inline-flex items-center gap-1">
                  <Flag
                    size={12}
                    aria-hidden
                    style={{ color: ATTENTION_COLOR }}
                  />
                  途絶えた会社
                </span>
              }
              value={formatCount(counts.lapsed)}
              sub={`以前は反応 → ${days} 日間なし`}
            />
            <Metric
              label={companyStateLabel("never_responded")}
              value={formatCount(counts.neverResponded)}
              sub="配信はしている"
            />
            <Metric
              label={companyStateLabel("not_delivered")}
              value={formatCount(counts.notDelivered)}
            />
          </div>

          <div className="mt-3 flex h-3 overflow-hidden rounded">
            {bands.map((band, index) => (
              <i
                key={band.state}
                className="block h-full"
                style={{
                  width: `${shareWidth(band.value)}%`,
                  background: BREAKDOWN_BAND_COLORS[index],
                }}
              />
            ))}
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-3 text-[10.5px] text-slate-600">
            {bands.map((band, index) => (
              <LegendItem
                key={band.state}
                color={BREAKDOWN_BAND_COLORS[index]}
                label={`${companyStateLabel(band.state)} ${band.value}（${formatRate(shareRate(band.value))}）`}
                flag={band.flag}
              />
            ))}
            <span className="ml-auto rounded-full border border-slate-200 px-2.5 py-0.5 text-slate-500">
              ＋ {companyStateLabel("not_delivered")} {counts.notDelivered} 社
            </span>
          </div>

          <div className="my-3 border-t border-slate-200" />

          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-sm font-semibold">会社別</h3>
            <div className="flex gap-4 border-b border-slate-200">
              {SORT_OPTIONS.map(option => (
                <TabButton
                  key={option.value}
                  active={option.value === sort}
                  onClick={() => setSort(option.value)}
                >
                  {option.label}
                </TabButton>
              ))}
            </div>
          </div>

          <div className="mt-2 overflow-x-auto">
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>会社</th>
                  <th>関心度</th>
                  <th>直近 {days} 日</th>
                  <th>最後のやりとり</th>
                  <th>次の一手</th>
                </tr>
              </thead>
              <tbody>
                {data.items.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="py-6 text-center text-base-content/60"
                    >
                      対象の会社がありません
                    </td>
                  </tr>
                ) : (
                  data.items.map(item => (
                    <CompanyRow key={item.companyId} item={item} />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

type CompanyRowProps = {
  item: CompanyActivityItem;
};

/**
 * 会社別一覧の 1 行。
 * @param {CompanyRowProps} props 会社の状態
 * @return {JSX.Element} 行
 */
const CompanyRow: FC<CompanyRowProps> = ({ item }) => {
  const dots = item.dots.map(dot => dot.reaction);
  const isLapsed = item.state === "lapsed";
  const step = companyReactionStep(dots);
  const tint = isLapsed ? null : REACTION_SCALE[step].tint;
  const chip = item.interestLevel ? INTEREST_CHIP[item.interestLevel] : null;
  const action = nextActionFor(item.interestLevel);
  const target =
    action.target === "replies"
      ? "/emails/replies"
      : `/settings/companies/${item.companyId}`;

  return (
    <tr style={{ background: tint ?? undefined }}>
      <td
        style={{
          borderLeft: `3px solid ${isLapsed ? ATTENTION_COLOR : REACTION_SCALE[step].color}`,
        }}
        className={cn(isLapsed && "font-bold")}
      >
        {isLapsed && (
          <Flag
            size={12}
            aria-hidden
            className="mr-1 inline"
            style={{ color: ATTENTION_COLOR }}
          />
        )}
        {item.companyName}
      </td>
      <td>
        {chip ? (
          <span
            className="inline-flex items-center gap-1 text-[10.5px] font-bold"
            style={{ color: chip.text }}
          >
            <i
              className="block h-2 w-2 rounded-full"
              style={{ background: chip.dot }}
            />
            {chip.flag && (
              <Flag size={12} aria-hidden style={{ color: chip.text }} />
            )}
            {chip.label}
          </span>
        ) : (
          <span style={{ color: NO_DATA_COLOR }}>—</span>
        )}
      </td>
      <td>
        <span className="flex items-end gap-0.5">
          {item.dots.map(dot => (
            <i
              key={dot.index}
              className="block w-[7px] rounded-sm"
              style={{
                height: ACTIVITY_DOT[dot.reaction].height,
                background: ACTIVITY_DOT[dot.reaction].color,
              }}
            />
          ))}
        </span>
      </td>
      <td className="text-xs">
        {item.lastReactionAt ? (
          `${formatRelativeDay(item.lastReactionAt)} · ${
            REACTION_TYPE_LABEL[item.lastReactionType ?? ""] ?? "反応"
          }`
        ) : (
          <span style={{ color: NO_DATA_COLOR }}>—</span>
        )}
      </td>
      <td className="text-xs">
        <Link
          to={target}
          className="hover:underline"
          style={
            isLapsed ? { color: ATTENTION_COLOR, fontWeight: 700 } : undefined
          }
        >
          {action.text}
        </Link>
      </td>
    </tr>
  );
};

type MetricProps = {
  label: React.ReactNode;
  value: React.ReactNode;
  sub?: string;
};

/**
 * 状態の内訳 KPI 1 枚。
 * @param {MetricProps} props ラベル・値・補助行
 * @return {JSX.Element} KPI
 */
const Metric: FC<MetricProps> = ({ label, value, sub }) => (
  <div>
    <div className="text-xs text-base-content/60">{label}</div>
    <div className="text-lg font-bold">{value}</div>
    {sub && <div className="text-[11px] text-base-content/50">{sub}</div>}
  </div>
);
